import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { normalizeRole } from '@/utils/permissions';
import { authApi, getToken, setToken, normalizeUser } from '@/lib/api';
import { canUseViewSelector } from '@/utils/founder';
import { hydrateUploadsFromBackend } from '@/utils/uploads';
import { hydrateFollowsFromBackend } from '@/utils/follows';
import { hydrateNotificationsFromBackend } from '@/utils/notifications';
import { hydrateUsersFromBackend } from '@/utils/usersHydrate';
import { tr } from '@/contexts/LanguageContext';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Migra usuario legacy: 'spectator' → 'client'. Idempotente.
const migrateLegacyRole = (raw) => {
  if (!raw) return raw;
  const normalized = normalizeRole(raw.role);
  if (normalized === raw.role) return raw;
  return { ...raw, role: normalized };
};

// Migra el registry global lineage_users (spectator → client, founder_admin → admin)
const migrateUsersRegistry = () => {
  try {
    const raw = localStorage.getItem('lineage_users');
    if (!raw) return;
    const reg = JSON.parse(raw);
    let dirty = false;
    Object.keys(reg).forEach((k) => {
      const u = reg[k];
      if (u && u.role === 'spectator') {
        reg[k] = { ...u, role: 'client' };
        dirty = true;
      } else if (u && u.role === 'founder_admin') {
        reg[k] = { ...u, role: 'admin' };
        dirty = true;
      }
    });
    if (dirty) localStorage.setItem('lineage_users', JSON.stringify(reg));
  } catch { /* ignore */ }
};

// BETA HONESTY CLEANUP — one-shot wipe of every localStorage key that older
// builds populated with mock/seed/fake data (notifications, tips, votes,
// live battles, tournaments, follow graph edges to mock barbers, etc.) and
// every "already seeded" marker so the seed routines (which are now
// no-ops) re-execute their cleanup logic on next boot. Idempotent — runs
// exactly once per device courtesy of the GLOBAL_CLEANUP_FLAG.
const GLOBAL_CLEANUP_FLAG = 'lineage_beta_honesty_cleanup_v2';
const MOCK_BARBER_USERNAMES = new Set([
  'fade_master', 'style_king', 'classic_cuts', 'beard_boss', 'sharp_edges',
  'design_pro', 'urban_cuts', 'precision_fade'
]);

const wipeBetaSeedData = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (localStorage.getItem(GLOBAL_CLEANUP_FLAG)) return;
    // 1. Drop synthetic tournaments + live battles (they were seeded with
    //    mock barbers; backend hydration will repopulate any real ones).
    //    Also drop the v2 admin-stats key + any prior cleanup flag.
    localStorage.removeItem('lineage_tournaments');
    localStorage.removeItem('lineage_tournaments_seed_v');
    localStorage.removeItem('lineage_tournaments_v2');
    localStorage.removeItem('lineage_live_battles');
    localStorage.removeItem('lineage_live_battles_cleaned_v3');
    localStorage.removeItem('lineage_beta_honesty_cleanup_v1');

    // 2. Drop seeded notifications (anything tagged `ntf_seed_*`) and reset
    //    the per-user seeded flags so the cleaner runs again on next login.
    try {
      const notifs = JSON.parse(localStorage.getItem('lineage_notifications') || '[]');
      const cleaned = Array.isArray(notifs)
        ? notifs.filter((n) => !(n && typeof n.id === 'string' && n.id.startsWith('ntf_seed_')))
        : [];
      localStorage.setItem('lineage_notifications', JSON.stringify(cleaned));
    } catch { /* ignore */ }

    // 3. Drop seeded courses/products. The dedicated cleaner will run too,
    //    but doing it here keeps the very first paint honest.
    try {
      const courses = JSON.parse(localStorage.getItem('lineage_courses') || '[]');
      localStorage.setItem('lineage_courses', JSON.stringify(
        courses.filter((c) => !(c && typeof c.id === 'string' && c.id.startsWith('course_seed_')))
      ));
    } catch { /* ignore */ }
    try {
      const products = JSON.parse(localStorage.getItem('lineage_barber_products') || '[]');
      localStorage.setItem('lineage_barber_products', JSON.stringify(
        products.filter((p) => !(p && typeof p.id === 'string' && p.id.startsWith('product_seed_')))
      ));
    } catch { /* ignore */ }

    // 4. Strip mock-barber edges from the follow graph.
    try {
      const graph = JSON.parse(localStorage.getItem('lineage_follow_graph') || '{}');
      let mutated = false;
      Object.keys(graph).forEach((follower) => {
        if (MOCK_BARBER_USERNAMES.has(follower)) {
          delete graph[follower];
          mutated = true;
          return;
        }
        if (!Array.isArray(graph[follower])) return;
        const filtered = graph[follower].filter((u) => !MOCK_BARBER_USERNAMES.has(u));
        if (filtered.length !== graph[follower].length) {
          graph[follower] = filtered;
          mutated = true;
        }
      });
      if (mutated) localStorage.setItem('lineage_follow_graph', JSON.stringify(graph));
    } catch { /* ignore */ }

    // 5. Wipe legacy admin-content seed items (createdBy === 'system_seed').
    try {
      const items = JSON.parse(localStorage.getItem('lineage_admin_content') || '[]');
      const surviving = items.filter(
        (c) => !(c && c.createdBy && String(c.createdBy.username || '').includes('system_seed'))
      );
      localStorage.setItem('lineage_admin_content', JSON.stringify(surviving));
    } catch { /* ignore */ }

    // 6. Drop per-user "already seeded" markers (notifications + follows +
    //    professional panel) so the now-cleaner functions re-run.
    Object.keys(localStorage).forEach((k) => {
      if (
        k.startsWith('lineage_notif_seeded_') ||
        k.startsWith('lineage_follow_seeded_') ||
        k.startsWith('lineage_pro_seed_') ||
        k.startsWith('lineage_academy_shop_booted_')
      ) {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      }
    });

    localStorage.setItem(GLOBAL_CLEANUP_FLAG, '1');
  } catch { /* ignore */ }
};

const persistLocal = (user) => {
  try {
    if (user) localStorage.setItem('lineage_user', JSON.stringify(user));
    else localStorage.removeItem('lineage_user');
  } catch { /* ignore */ }
};

// ============================================================
// VISTA FIEL (mandato Founder, jun-2026) — rol de vista persistido.
//
// El cambio de rol del founder es un "rol de vista" que se guarda en
// localStorage y se RE-APLICA sobre cualquier usuario que llegue del
// backend (boot /auth/me, refresh por membresía, respuesta de PATCH
// /users/me). Antes, cualquiera de esos re-syncs pisaba el rol de vista
// con `super_admin` y el founder saltaba de vuelta a la vista admin
// (p.ej. al arrastrar/reordenar la pestaña de agenda, cuyo PATCH de
// profile_tab_order devolvía el usuario real).
// ============================================================
const VIEW_ROLE_KEY = 'lineage_view_role';

const getViewRole = () => {
  try { return localStorage.getItem(VIEW_ROLE_KEY) || null; } catch { return null; }
};

const setViewRoleStorage = (role) => {
  try {
    if (role) localStorage.setItem(VIEW_ROLE_KEY, role);
    else localStorage.removeItem(VIEW_ROLE_KEY);
  } catch { /* ignore */ }
};

// Aplica el rol de vista sobre un usuario (crudo del backend o cacheado).
// Devuelve el usuario con `role` = rol de vista y `real_role` = identidad
// real. Si no hay vista activa (o el usuario no puede cambiar de rol),
// devuelve el usuario con su rol real y limpia el override.
const applyViewRole = (raw) => {
  if (!raw) return raw;
  const base = raw.real_role ? { ...raw, role: raw.real_role } : { ...raw };
  delete base.real_role;
  const viewRole = getViewRole();
  if (!viewRole) return base;
  if (!canUseViewSelector(base)) { setViewRoleStorage(null); return base; }
  if (viewRole === base.role) { setViewRoleStorage(null); return base; }
  return { ...base, role: viewRole, real_role: base.role };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Sube en cada login/registro: señal estable de "sesión nueva" (misma cuenta incluida).
  const [sessionVersion, setSessionVersion] = useState(0);

  // Iter 134 — Cookie-only bootstrap.
  //
  // The JWT now lives ONLY inside an httpOnly cookie the browser stores
  // opaquely. JS cannot read it. So we cannot "check if a token exists"
  // up-front anymore — the source of truth is whether GET /api/auth/me
  // succeeds. We always fire that call on mount; a 401 is interpreted
  // as "no session" and we fall back to the localStorage-cached user
  // shape (offline UX) when present.
  useEffect(() => {
    wipeBetaSeedData();
    migrateUsersRegistry();
    // Public-facing surfaces (Rankings/Discover/Live Battles) don't require auth.
    // Hydrate the users registry from the backend on every app boot so those
    // surfaces show real barbers + the seeded demo accounts before login.
    hydrateUsersFromBackend().catch(() => { /* offline OK */ });
    const cached = (() => {
      try { return JSON.parse(localStorage.getItem('lineage_user') || 'null'); }
      catch { return null; }
    })();

    // Defensive: nuke any legacy token a previous build may have stashed.
    // From here on the cookie is the only carrier.
    setToken(null);

    authApi.me()
      .then((u) => {
        const migrated = applyViewRole(migrateLegacyRole(u));
        setUser(migrated);
        persistLocal(migrated);
        // Wave D: hydrate uploads/posts cache from backend.
        hydrateUploadsFromBackend(migrated).catch(() => { /* offline OK */ });
        // Wave E: hydrate follows graph from backend.
        hydrateFollowsFromBackend(migrated).catch(() => { /* offline OK */ });
        // Wave F: hydrate notifications cache from backend.
        hydrateNotificationsFromBackend(migrated).catch(() => { /* offline OK */ });
      })
      .catch((err) => {
        // 401 = NO hay sesión (caducó o la contraseña cambió): se descarta la copia local para
        // no quedar en un estado «zombi» que dispara 401 en cadena (y la redirección a /auth).
        // Solo sin red (status 0) se usa la copia local para no dejar la pantalla en blanco.
        if (err?.status === 401) {
          setUser(null);
          persistLocal(null);
        } else if (cached) {
          setUser(applyViewRole(migrateLegacyRole(cached)));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * Backend login. Call with (email, password). Returns the user on success.
   * Throws an Error on failure (with `.status` and `.payload`).
   */
  const adoptSession = useCallback((u) => {
    // Sesión nueva ⇒ vista fiel se resetea al rol real.
    setViewRoleStorage(null);
    const migrated = migrateLegacyRole(u);
    setUser(migrated);
    persistLocal(migrated);
    setSessionVersion((v) => v + 1);
    // Iter 138 — disarm the redirect-loop sentinel so a future stale
    // session (e.g. user idles past the JWT TTL) can route to /auth
    // again cleanly.
    if (typeof window !== 'undefined') window.__lineage_session_redirect_armed__ = false;
    hydrateUploadsFromBackend(migrated).catch(() => { /* offline OK */ });
    hydrateFollowsFromBackend(migrated).catch(() => { /* offline OK */ });
    hydrateNotificationsFromBackend(migrated).catch(() => { /* offline OK */ });
    return migrated;
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    if (data.mfa_required) return { mfa_required: true };
    return adoptSession(data.user);
  }, [adoptSession]);

  // 2FA — segundo paso del login (código TOTP o de respaldo).
  const loginMfa = useCallback(async (code, rememberDevice = false) => {
    const data = await authApi.mfaVerify(code, rememberDevice);
    const u = adoptSession(data.user);
    return { user: u, backup_code_used: data.backup_code_used, backup_codes_left: data.backup_codes_left };
  }, [adoptSession]);

  /**
   * Backend register. Body must include {email, password, name, username, role, country, state?, city?}.
   */
  const register = useCallback(async (payload) => {
    const { user: u } = await authApi.register(payload);
    setViewRoleStorage(null);
    const migrated = migrateLegacyRole(u);
    setUser(migrated);
    persistLocal(migrated);
    setSessionVersion((v) => v + 1);
    // Iter 138 — disarm the redirect-loop sentinel (see comment in login).
    if (typeof window !== 'undefined') window.__lineage_session_redirect_armed__ = false;
    hydrateUploadsFromBackend(migrated).catch(() => { /* offline OK */ });
    hydrateFollowsFromBackend(migrated).catch(() => { /* offline OK */ });
    hydrateNotificationsFromBackend(migrated).catch(() => { /* offline OK */ });
    return migrated;
  }, []);

  /** Backend logout (clears token + localStorage). */
  const logout = useCallback(async () => {
    // 21-oct-2026 — este dispositivo deja de recibir los push del usuario que cierra sesión (teléfono compartido).
    try { const { disablePush } = await import('@/pwa/push'); await disablePush({ all: false }); } catch { /* sin push en este navegador */ }
    await authApi.logout();
    setViewRoleStorage(null);
    setUser(null);
    persistLocal(null);
  }, []);

  /** Bloque 3 — adopta una sesión creada por otro endpoint (p. ej. reserva sin sesión ⇒ cookies ya puestas). */
  const refreshSession = useCallback(async () => {
    const u = await authApi.me();
    setViewRoleStorage(null);
    const migrated = migrateLegacyRole(u);
    setUser(migrated);
    persistLocal(migrated);
    setSessionVersion((v) => v + 1);
    if (typeof window !== 'undefined') window.__lineage_session_redirect_armed__ = false;
    hydrateNotificationsFromBackend(migrated).catch(() => { /* offline OK */ });
    return migrated;
  }, []);

  // Iter 117 — listen for membership changes (subscribe/buy-tokens) and
  // re-pull /api/auth/me so any wallet UI that reads `user.membership_tier`
  // (e.g. the wallet-current-tier-badge inside WalletSection) flips
  // immediately, not on the next manual refresh.
  useEffect(() => {
    if (!user) return undefined;
    const refreshFromBackend = async () => {
      try {
        const fresh = await authApi.me();
        const migrated = applyViewRole(migrateLegacyRole(fresh));
        setUser(migrated);
        persistLocal(migrated);
      } catch {
        /* offline → keep current state */
      }
    };
    window.addEventListener('lineage_membership_updated', refreshFromBackend);
    return () => window.removeEventListener('lineage_membership_updated', refreshFromBackend);
  }, [user?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Update profile locally. Optionally also PATCHes the backend via /api/users/me.
   * Callers that want pure local state can pass {local:true} as the second arg.
   *
   * Defense-in-depth: role mutations are only honoured for the founder /
   * super_admin. A normal barber that tries `updateUser({role:'admin'})`
   * (via stale UI or a tampered call) will see the role stripped silently
   * so beta accounts can't escalate their privileges through localStorage.
   */
  const updateUser = useCallback(async (updates, opts = {}) => {
    const safeUpdates = { ...(updates || {}) };
    if (safeUpdates.role !== undefined && safeUpdates.role !== user?.role) {
      const isFounder = canUseViewSelector(user);
      if (!isFounder) {
        delete safeUpdates.role;
      }
    }
    // Vista fiel — un cambio de rol del founder se persiste como rol de
    // vista (sobrevive recargas y re-syncs). Volver al rol real lo limpia.
    let merged;
    if (safeUpdates.role !== undefined) {
      const realRole = user?.real_role || user?.role;
      const nextRole = normalizeRole(safeUpdates.role);
      if (nextRole === realRole) {
        setViewRoleStorage(null);
        merged = migrateLegacyRole({ ...user, ...safeUpdates, role: realRole });
        delete merged.real_role;
      } else {
        setViewRoleStorage(nextRole);
        merged = { ...migrateLegacyRole({ ...user, ...safeUpdates, role: realRole }), role: nextRole, real_role: realRole };
      }
    } else {
      merged = migrateLegacyRole({ ...user, ...safeUpdates });
    }
    setUser(merged);
    persistLocal(merged);
    // Beta Sprint 2 (P1-3/P1-5 root cause) — auth vive en cookie httpOnly,
    // getToken() SIEMPRE es null desde que se retiró el token de localStorage.
    // El bail-out `!getToken()` hacía que el PATCH /users/me jamás se
    // ejecutara: avatares/bio/nombre quedaban solo en localStorage y el
    // resto de usuarios nunca los veía. La sesión la valida el backend.
    if (opts.local) return merged;
    try {
      // Only send fields that the backend cares about.
      const patch = {};
      ['name', 'username', 'bio', 'avatar', 'cover', 'country', 'state', 'city', 'region',
       'phone', 'email', 'instagram', 'tiktok', 'facebook', 'youtube', 'website',
       'specialty', 'profile_tab_order', 'profile_default_tab', 'profession', 'profession_other', 'interests'].forEach((k) => {
        if (safeUpdates[k] !== undefined) patch[k] = safeUpdates[k];
      });
      if (safeUpdates.rankingDisplay !== undefined) patch.ranking_display = safeUpdates.rankingDisplay;
      if (safeUpdates.shopName !== undefined) patch.shop_name = safeUpdates.shopName;
      if (Object.keys(patch).length) {
        const headers = { 'Content-Type': 'application/json' };
        const bearer = getToken();
        if (bearer) headers.Authorization = `Bearer ${bearer}`;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify(patch),
        });
        if (!res.ok) {
          // Iter 149 — propagate HTTP failures so callers (e.g. the
          // register modal) can react to 409 email collisions instead
          // of blindly showing the optimistic state as truth.
          let detail = tr("PATCH /users/me failed");
          try {
            const body = await res.json();
            detail = body?.detail || detail;
          } catch { /* non-JSON body */ }
          const err = new Error(detail);
          err.status  = res.status;
          err.payload = { detail };
          throw err;
        }
        const saved = await res.json();
        // Vista fiel — la respuesta del backend trae el rol REAL; re-aplicar
        // el rol de vista para que un PATCH (p.ej. reordenar pestañas de
        // agenda) no expulse al founder de la vista de barbero.
        const fresh = applyViewRole(migrateLegacyRole(normalizeUser(saved)));
        setUser(fresh);
        persistLocal(fresh);
        return fresh;
      }
    } catch (err) {
      // Propagate 4xx so callers can present a specific message; only
      // swallow network / 5xx (transient) errors to keep the optimistic
      // local state alive during flaky connectivity.
      if (err?.status && err.status >= 400 && err.status < 500) {
        throw err;
      }
      console.warn('[Auth] PATCH /users/me failed:', err);
    }
    return merged;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, loginMfa, register, logout, refreshSession, updateUser, loading, sessionVersion }}>
      {children}
    </AuthContext.Provider>
  );
};
